// Store
import { useTemplateStore } from "@/zustand/templateStore";

// Components
import PaypalButton from "@/components/Buttons/PaypalButton";
import ApplePayButton from "@/components/Buttons/ApplePayButton";
import GooglePayButton from "@/components/Buttons/GooglePayButton";
import Box from "@/components/Box";

// Assets
import paypalIcon from "@/assets/paypal.svg";

interface ExpressCheckoutButtonsProps {
  handlePaypalSubmit?: () => Promise<void>;
  onApplePaySubmit?: (
    data: any,
    options?: { signal: AbortSignal },
  ) => Promise<void>;
  onGooglePaySubmit?: (
    data: any,
    options?: { signal: AbortSignal },
  ) => Promise<void>;
  activePaypal: boolean;
  activeApplePay: boolean;
  activeGooglePay: boolean;
}

const ExpressCheckoutButtons = ({
  onApplePaySubmit,
  onGooglePaySubmit,
  handlePaypalSubmit,
  activePaypal,
  activeApplePay,
  activeGooglePay,
}: ExpressCheckoutButtonsProps) => {
  const { form, settings } = useTemplateStore();

  const activeButtons = [
    activePaypal ? "paypal" : null,
    activeApplePay ? "apple" : null,
    activeGooglePay ? "google" : null,
  ].filter(Boolean);

  const count = activeButtons.length;

  if (count === 0) return null;

  return (
    <Box
      className={`md:shadow-cc relative gap-4 px-3 py-5 md:px-4 md:py-6 ${
        !form?.rounded_borders ? "rounded-none" : ""
      }`}
    >
      <div className="border-cc-gray-4 flex flex-col gap-4 rounded-md md:border md:p-5">
        <div className="absolute -top-3 flex w-full justify-center md:top-3 md:right-[35%] md:left-auto md:w-auto md:justify-end">
          <span
            className={`bg-white px-4 text-sm whitespace-nowrap ${
              settings.enable_high_contrast
                ? "text-cc-gray-6"
                : "text-cc-gray-3"
            }`}
          >
            {form?.tr_express_checkout_label}
          </span>
        </div>
        {count === 3 ? (
          <>
            <div className="flex flex-col gap-3 md:hidden">
              {activePaypal && (
                <PaypalButton
                  handlePaypalSubmit={() =>
                    Promise.resolve(handlePaypalSubmit!())
                  }
                  height={24}
                  icon={paypalIcon}
                  className={`bg-cc-yellow-2 w-full cursor-pointer rounded-md py-3 ${settings.enable_high_contrast ? "border-cc-orange border-b-2" : ""}`}
                />
              )}
              <div className="flex w-full flex-row gap-3">
                {activeApplePay && (
                  <ApplePayButton
                    onApplePaySubmit={onApplePaySubmit}
                    height={24}
                    className="w-1/2 cursor-pointer rounded-md bg-black py-3"
                  />
                )}
                {activeGooglePay && (
                  <GooglePayButton
                    onGooglePaySubmit={onGooglePaySubmit}
                    height={24}
                    className="w-1/2 cursor-pointer rounded-md bg-black py-3"
                  />
                )}
              </div>
            </div>
            <div className="hidden md:flex md:flex-row md:gap-3">
              {activeApplePay && (
                <ApplePayButton
                  onApplePaySubmit={onApplePaySubmit}
                  height={24}
                  className="flex-1 cursor-pointer rounded-md bg-black py-3"
                />
              )}
              {activePaypal && (
                <PaypalButton
                  handlePaypalSubmit={() =>
                    Promise.resolve(handlePaypalSubmit!())
                  }
                  height={24}
                  icon={paypalIcon}
                  className={`bg-cc-yellow-2 flex-1 cursor-pointer rounded-md py-3 ${settings.enable_high_contrast ? "border-cc-orange border-b-2" : ""}`}
                />
              )}
              {activeGooglePay && (
                <GooglePayButton
                  onGooglePaySubmit={onGooglePaySubmit}
                  height={24}
                  className="flex-1 cursor-pointer rounded-md bg-black py-3"
                />
              )}{" "}
            </div>
          </>
        ) : (
          <div className="flex flex-row gap-3">
            {activePaypal && (
              <PaypalButton
                handlePaypalSubmit={() =>
                  Promise.resolve(handlePaypalSubmit!())
                }
                height={24}
                icon={paypalIcon}
                className={`bg-cc-yellow-2 cursor-pointer rounded-md py-3 ${
                  count === 1 ? "w-full" : "w-1/2 md:w-1/2"
                } ${settings.enable_high_contrast && "border-cc-orange border-b-2"}`}
              />
            )}
            {activeApplePay && (
              <ApplePayButton
                onApplePaySubmit={onApplePaySubmit}
                height={24}
                className={`cursor-pointer rounded-md bg-black py-3 ${
                  count === 1 ? "w-full" : "w-1/2 md:w-1/2"
                }`}
              />
            )}
            {activeGooglePay && (
              <GooglePayButton
                onGooglePaySubmit={onGooglePaySubmit}
                height={24}
                className={`cursor-pointer rounded-md bg-black py-3 ${
                  count === 1 ? "w-full" : "w-1/2 md:w-1/2"
                }`}
              />
            )}
          </div>
        )}

        <div className="absolute -bottom-3 flex w-full justify-center md:right-[25%] md:bottom-3 md:left-auto md:w-auto md:justify-end">
          <span
            className={`bg-white px-4 text-sm whitespace-nowrap ${
              settings.enable_high_contrast
                ? "text-cc-gray-6"
                : "text-cc-gray-3"
            }`}
          >
            {form?.tr_secondary_express_checkout_label}
          </span>
        </div>
      </div>
    </Box>
  );
};

export default ExpressCheckoutButtons;
